<!-- src/components/common/BaseForm.vue -->
<template>
  <form @submit.prevent="onSubmit">
    <!-- Loop through each field definition provided via props -->
    <div v-for="field in fields" :key="field.key" class="mb-3">
      <!-- Label for the field -->
  <label
    v-if="field.type !== 'checkbox'"
    :for="`baseform-${field.key}`"
    class="form-label"
  >
    {{ field.label }}
    <span v-if="field.required" class="text-danger">*</span>
  </label>

      <!-- Group conditional rendering using <template> to avoid v-else/v-else-if errors -->
      <template
        v-if="
          field.type === 'text' ||
          field.type === 'email' ||
          field.type === 'number' ||
          field.type === 'date'
        "
      >
        <!-- Text, Email, Number Input -->
        <input
          :id="`baseform-${field.key}`"
          :type="field.type"
          class="form-control"
          :class="{ 'form-control-plaintext': field.readonly }"
          :placeholder="field.placeholder"
          v-model="localFormData[field.key]"
          :required="field.required"
          :readonly="field.readonly"
        />
      </template>

      <template v-else-if="field.type === 'textarea'">
        <!-- Textarea -->
        <textarea
          :id="`baseform-${field.key}`"
          class="form-control"
          :class="{ 'form-control-plaintext': field.readonly }"
          :placeholder="field.placeholder"
          v-model="localFormData[field.key]"
          :required="field.required"
          :readonly="field.readonly"
          :rows="field.rows || 3"
        ></textarea>
      </template>

      <template v-else-if="field.type === 'color'">
        <!-- Color Picker (Edit Mode) -->
        <input
          v-if="!field.readonly"
          :id="`baseform-${field.key}`"
          type="color"
          class="form-control form-control-color"
          v-model="localFormData[field.key]"
        />
        <!-- Color Display (View Mode) -->
        <div v-else>
          <span
            class="badge"
            :style="{ backgroundColor: localFormData[field.key] }"
          >
            {{ localFormData[field.key] }}
          </span>
        </div>
      </template>

      <template v-else-if="field.type === 'file'">
        <!-- File Info Display (View Mode) -->
        <div v-if="field.readonly && localFormData[field.key]">
          <div v-if="typeof localFormData[field.key] === 'string' && (localFormData[field.key].match(/\.(jpg|jpeg|png|gif|webp|svg)/i) || localFormData[field.key].startsWith('http'))">
            <img :src="localFormData[field.key]" alt="Preview" class="img-thumbnail mb-2" style="max-height: 150px; display: block;" />
          </div>
          <span v-else class="text-muted">
            File:
            {{
              localFormData[field.key] && (localFormData[field.key] as File).name
                ? (localFormData[field.key] as File).name
                : localFormData[field.key]
            }}
          </span>
        </div>
        <!-- File Input (Edit Mode) -->
        <div v-else>
          <!-- Existing Image Preview -->
          <div v-if="typeof localFormData[field.key] === 'string' && (localFormData[field.key].match(/\.(jpg|jpeg|png|gif|webp|svg)/i) || localFormData[field.key].startsWith('http'))" class="mb-2">
            <img :src="localFormData[field.key]" alt="Current" class="img-thumbnail" style="max-height: 100px;" />
            <div class="small text-muted">Current image</div>
          </div>

          <input
            :id="`baseform-${field.key}`"
            type="file"
            class="form-control"
            @change="(e) => handleFileChange(e, field.key)"
            :accept="field.accept || '*'"
            :required="field.required && !localFormData[field.key]"
          />
          
          <!-- Selected File Info Feedback -->
          <div
            v-if="localFormData[field.key] && (localFormData[field.key] as File).name"
            class="mt-2"
          >
            <span class="text-muted">Selected: {{ (localFormData[field.key] as File).name }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="field.type === 'select'">
        <select
          :id="`baseform-${field.key}`"
          class="form-select"
          v-model="localFormData[field.key]"
          :required="field.required"
          :disabled="field.readonly"
        >
          <option disabled value="">-- Select --</option>
          <option
            v-for="option in field.options"
            :key="option._id"
            :value="option._id"
          >
            {{ option.title }}
          </option>
        </select>
      </template>
      <!-- Fallback for unknown field types -->

      <template v-else-if="field.type === 'chip'">
        <div>
          <div class="input-group mb-2" v-if="!field.readonly">
            <input
              type="text"
              class="form-control"
              :placeholder="field.placeholder"
              v-model="chipInput"
              @keydown.enter.prevent="addChip(field.key)"
            />
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="addChip(field.key)"
            >
              Add
            </button>
          </div>
          <div class="d-flex flex-wrap gap-1">
            <span
              v-for="(chip, index) in localFormData[field.key]"
              :key="index"
              class="badge bg-primary d-flex align-items-center"
            >
              {{ chip }}
              <i
                v-if="!field.readonly"
                class="ms-2 cursor-pointer"
                @click="removeChip(field.key, index)"
                >×</i
              >
            </span>
          </div>
        </div>
      </template>
<template v-else-if="field.type === 'checkbox'">
  <div class="form-check">
    <input
      type="checkbox"
      v-model="model[field.key]"
      class="form-check-input"
      :id="`baseform-${field.key}`"
      :disabled="field.readonly"
    />
    <label class="form-check-label" :for="`baseform-${field.key}`">
      {{ field.label }}
      <span v-if="field.required" class="text-danger">*</span>
    </label>
  </div>
</template>

<template v-else-if="field.type === 'switch'">
  <div class="form-check form-switch mt-2">
    <input
      type="checkbox"
      v-model="model[field.key]"
      class="form-check-input"
      role="switch"
      :id="`baseform-${field.key}`"
      :disabled="field.readonly"
    />
    <label class="form-check-label" :for="`baseform-${field.key}`">
      {{ field.label }}
      <span v-if="field.required" class="text-danger">*</span>
    </label>
  </div>
</template>

<template v-else-if="field.type === 'multiselect'">
  <div class="multiselect-wrapper" v-if="!field.readonly">
    <div
      class="multiselect-trigger form-control d-flex align-items-center justify-content-between"
      @click="toggleMultiselect(field.key)"
    >
      <span v-if="!localFormData[field.key] || localFormData[field.key].length === 0" class="text-muted">
        {{ field.placeholder || '-- Select --' }}
      </span>
      <span v-else class="multiselect-selected-text">
        {{ localFormData[field.key].length }} selected
      </span>
      <i class="fas fa-chevron-down ms-2" style="font-size: 0.7rem; color: #6c757d;"></i>
    </div>
    <div v-if="openMultiselect === field.key" class="multiselect-dropdown">
      <div
        v-for="option in field.options"
        :key="option._id"
        class="multiselect-option"
        @click.stop="toggleMultiselectOption(field.key, option._id)"
      >
        <input
          type="checkbox"
          class="form-check-input me-2"
          :checked="(localFormData[field.key] || []).includes(option._id)"
          @click.stop
          @change="toggleMultiselectOption(field.key, option._id)"
        />
        <span>{{ option.title }}</span>
      </div>
      <div v-if="!field.options || field.options.length === 0" class="multiselect-option text-muted">
        No options available
      </div>
    </div>
    <div class="d-flex flex-wrap gap-1 mt-2" v-if="localFormData[field.key] && localFormData[field.key].length">
      <span
        v-for="selectedId in localFormData[field.key]"
        :key="selectedId"
        class="badge bg-primary d-flex align-items-center"
      >
        {{ (field.options || []).find((o: any) => o._id === selectedId)?.title || selectedId }}
        <i class="ms-2 cursor-pointer" @click="removeMultiselectOption(field.key, selectedId)">×</i>
      </span>
    </div>
  </div>
  <div v-else>
    <div class="d-flex flex-wrap gap-1">
      <span
        v-for="selectedId in (localFormData[field.key] || [])"
        :key="selectedId"
        class="badge bg-secondary"
      >
        {{ (field.options || []).find((o: any) => o._id === selectedId)?.title || selectedId }}
      </span>
    </div>
  </div>
</template>
    </div>

    <!-- Slot for any additional fields not defined in the 'fields' prop -->
    <slot name="additional-fields"></slot>
  </form>
</template>
<script lang="ts">
import { defineComponent } from "vue";
export default defineComponent({
  name: "BaseForm",
});
</script>
<script lang="ts" setup>
import { ref, watch } from "vue";

// --- Define the structure for form fields ---
export interface FormField {
  key: string;
  label: string;
  type: "text" | "email" | "number" | "textarea" | "color" | "file" | "select" | "checkbox" | "chip" | "date" | "multiselect" | "switch";
  placeholder?: string;
  required?: boolean;
  readonly?: boolean;
  rows?: number;
  accept?: string;
  options?: Array<{ _id: string; title: string }>;
}

const chipInput = ref("");

const addChip = (key: string) => {
  if (chipInput.value.trim()) {
    if (!localFormData.value[key]) localFormData.value[key] = [];
    localFormData.value[key].push(chipInput.value.trim());
    chipInput.value = "";
  }
};

const removeChip = (key: string, index: number) => {
  localFormData.value[key].splice(index, 1);
};

// --- Multiselect helpers ---
const openMultiselect = ref<string | null>(null);

const toggleMultiselect = (key: string) => {
  openMultiselect.value = openMultiselect.value === key ? null : key;
};

const toggleMultiselectOption = (key: string, optionId: string) => {
  if (!localFormData.value[key]) localFormData.value[key] = [];
  const idx = localFormData.value[key].indexOf(optionId);
  if (idx === -1) {
    localFormData.value[key] = [...localFormData.value[key], optionId];
  } else {
    localFormData.value[key] = localFormData.value[key].filter((id: string) => id !== optionId);
  }
};

const removeMultiselectOption = (key: string, optionId: string) => {
  localFormData.value[key] = localFormData.value[key].filter((id: string) => id !== optionId);
};
// --- Define component props ---
const props = defineProps<{
  fields: FormField[];
  // Note: initial-data prop is removed when using v-model
}>();

// --- Define v-model (requires Vue 3.4+) ---
// This creates an implicit modelValue prop and 'update:modelValue' event
const model = defineModel<Record<string, any>>({ required: true });

// --- Define component emits ---
// Corrected syntax: specify parameter name 'data' and its type
const emit = defineEmits<{
  (e: "submit", data: Record<string, any>): void;
}>();

// --- Component State ---
// Local copy for v-model binding within the form.
// Initialized with the value passed via v-model from the parent.
const localFormData = ref<Record<string, any>>({ ...model.value });

// --- Loop Prevention Flags ---
// These flags help prevent infinite update loops between localFormData and model.value.
let isUpdatingFromModel = false; // Tracks updates coming from the parent via v-model
let isUpdatingFromLocal = false; // Tracks updates coming from user input/local changes

// --- Watchers ---

/**
 * Watch for changes from the PARENT component (via v-model).
 * This updates the LOCAL state (localFormData) when the parent's data changes.
 */
watch(
  model,
  (newVal) => {
    // Prevent loop: If we just emitted an update locally, ignore the parent's echo
    if (isUpdatingFromLocal) {
      isUpdatingFromLocal = false; // Reset flag
      // console.log("BaseForm: Ignoring model update as it originated locally");
      return;
    }

    // Set flag to indicate this update is from the model (parent)
    isUpdatingFromModel = true;
    // console.log("BaseForm: Model changed from parent, updating localFormData:", newVal);

    // Update local copy. Using spread operator for a simple shallow copy.
    localFormData.value = { ...newVal };
  },
  { deep: true }
); // Deep watch is necessary for nested objects

/**
 * Watch for changes in the LOCAL form data (user input).
 * This emits 'update:modelValue' to the PARENT component when the form changes.
 */
watch(
  localFormData,
  (newVal) => {
    // Prevent loop: If we just received an update from the model, don't emit back
    if (isUpdatingFromModel) {
      isUpdatingFromModel = false; // Reset flag
      // console.log("BaseForm: Ignoring local update as it originated from model");
      return;
    }

    // Set flag to indicate this update is from local changes
    isUpdatingFromLocal = true;
    // console.log("BaseForm: localFormData changed, emitting update to parent:", newVal);

    // Emit the updated data back to the parent component via v-model
    // Emitting a copy ({ ...newVal }) is safer to prevent direct mutations by parent
    emit("update:modelValue", { ...newVal });
  },
  { deep: true }
); // Deep watch is necessary for nested objects

// --- Handlers ---

/**
 * Handles file input changes.
 * Updates localFormData and triggers the watcher to emit the change.
 */
const handleFileChange = (event: Event, key: string) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    localFormData.value[key] = target.files[0];
  } else {
    localFormData.value[key] = null;
  }
  // Modifying localFormData.value[key] will automatically trigger the
  // watch(localFormData, ...) watcher above, which will then emit the update.
};

/**
 * Handles form submission.
 * Performs basic validation and emits the 'submit' event with the current form data.
 */
const onSubmit = () => {
  // Basic validation check based on field definitions
  const isValid = props.fields.every((field) => {
    if (field.required) {
      const value = localFormData.value[field.key];
      if (field.type === "file") {
        // For required file inputs, check if a File object is present
        return value instanceof File;
      }
      // For other required fields, check if value exists and is not just whitespace
      return (
        value !== undefined && value !== null && value.toString().trim() !== ""
      );
    }
    // Non-required fields are considered valid by default
    return true;
  });

  if (isValid) {
    // If valid, emit the 'submit' event with the current form data
    emit("submit", { ...localFormData.value });
  } else {
    // If invalid, log a warning (parent component can handle UI feedback)
    console.warn("BaseForm: Form validation failed.");
  }
};

// --- Expose internal state (optional) ---
// Expose localFormData if the parent component needs direct access
defineExpose({ formData: localFormData });
</script>

<style scoped>
/* Scoped styles for the component */
.badge {
  font-size: 0.875em; /* Adjust size as needed */
}
/* Ensure readonly inputs look like plain text */
.form-control-plaintext {
  background-color: transparent;
  border: none;
  padding-left: 0;
  padding-right: 0;
}
/* Multiselect styles */
.multiselect-wrapper {
  position: relative;
}
.multiselect-trigger {
  cursor: pointer;
  min-height: 38px;
}
.multiselect-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1050;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.multiselect-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.15s;
}
.multiselect-option:hover {
  background: #f0f7ff;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
